import {
  BaseIdentifyingInformation,
  quickJsonFetch,
  IPayloadWebResponse,
  PayloadWebRequest,
  Address,
  ICoordinates
} from "../../business-objects/AlacrityInterfaces";
import { ICoordinatesExtended } from "../../business-objects/ExtendedCoordinates"
import IAddressCandidateViewModel from "../../business-objects/AddressCandidateViewModel";
import { IntersectionData } from "../../business-objects/IntersectionData";
import MappingAddressCandidateRequestViewModel from "../../business-objects/MappingAddressCandidateRequestViewModel";
import { MappingAddressResponse } from "../../business-objects/MappingAddressResponse";
import { MappingPointResponse } from "../../business-objects/MappingPointResponse";
import { MappingPostalCodeResponse } from "../../business-objects/MappingPostalCodeResponse";
import { MappingRoadwayResponse } from "../../business-objects/MappingRoadwayResponse";
import { PostalCodeData } from "../../business-objects/PostalCodeData";
import { RoadwayData } from "../../business-objects/RoadwayData";
import { IMapOptions } from "../../business-objects/MapOptions";
import LocationExtended from "../../business-objects/ExtendedLocation"

const feetInMiles: number = 5280.0;

export default class MapUtils {
  /**
   * @description geolocation object from browser, used to get current location
   */
  static geolocation: Geolocation = navigator.geolocation
    ? navigator.geolocation
    : {
        clearWatch(watchId: number) {
          console.log(`Your browser doesn't support geolocation.`);
        },

        getCurrentPosition(
          successCallback: PositionCallback,
          errorCallback?: PositionErrorCallback,
          options?: PositionOptions
        ) {
          console.log(`Your browser doesn't support geolocation.`);
        },

        watchPosition(successCallback: PositionCallback, errorCallback?: PositionErrorCallback, options?: PositionOptions) {
          console.log(`Your browser doesn't support geolocation.`);
          return -1;
        }
      };

  /**
   * @description Simple wrapper to HTML5 Geolocation getCurrentPosition().
   * @static
   * @returns {Promise<Coordinates>}
   * @memberof MapUtils
   */
  static getCurrentPosition(): Promise<ICoordinates> {
    return new Promise<ICoordinates>((resolve, reject) => {
      MapUtils.geolocation.getCurrentPosition(
        position => resolve(position.coords),
        error => {
          console.log(error);
          resolve(null);
        }
      );
    });
  }

  static getAddressData(
    coordinates: ICoordinates,
    externalUrl: string,
    userId: number,
    applicationId: number
  ): Promise<Address> {
    let identity = {
      webServiceUrl: "",
      agencyOri: "",
      token: "",
      userId: userId,
      applicationId: applicationId,
      versionId: ""
    };
    return quickJsonFetch<MappingAddressResponse>(
      externalUrl + "/API/Mapping/GetAddressData_FormIo",
      new PayloadWebRequest<ICoordinates>(identity, coordinates),
      ""
    ).then(data => data.address);
  }

  static async getReferencePostData( coordinates:ICoordinatesExtended, externalUrl: string) {
    const headers = new Headers([
      ['Accept', 'application/json, text/plain, */*'],
      ['Content-Type', 'application/json']
    ]);
    return await fetch(externalUrl + "/API/Mapping/GetReferencePostData",
                        { method: 'POST',
                          headers,
                          body: coordinates && JSON.stringify(coordinates) 
                        })
                  .then(response => {
                      if (!response.ok)
                        throw Error(response.statusText);
                      return response.json();
                    })
                  .then(result => result)
                  .catch(error => console.log(error))
  }

  /**
   * @description get intersecting street data from lat/long cords
   * @param {BaseIdentifyingInformation} identity - indentity of user/app
   * @param {ICoordinates} coordinates - coordinates
   * @returns {Promise<PostalCodeData[]>}
   */
  static getNearByPostalCodeData(
    identity: BaseIdentifyingInformation,
    coordinates: ICoordinates
  ): Promise<PostalCodeData[]> {
    return quickJsonFetch<MappingPostalCodeResponse>(
      identity.webServiceUrl + "/API/Mapping/GetNearByPostalCodeData",
      new PayloadWebRequest<ICoordinates>(identity, coordinates),
      identity.token
    ).then((data: MappingPostalCodeResponse) => data.postalCodes);
  }

  static getIntersectionData(
    coordinates: ICoordinates,
    externalUrl: string,
    userId: number,
    applicationId: number
  ): Promise<IntersectionData> {
    let identity = {
      webServiceUrl: "",
      agencyOri: "",
      token: "",
      userId: userId,
      applicationId: applicationId,
      versionId: ""
    };
    return quickJsonFetch<IPayloadWebResponse<IntersectionData>>(
      externalUrl + "/API/Mapping/GetIntersectionData_FormIo",
      new PayloadWebRequest<ICoordinates>(identity, coordinates),
      ""
    ).then((data: IPayloadWebResponse<IntersectionData>) => data.payload);
  }

  /**
   * @description get intersecting street data from lat/long cords
   * @param {BaseIdentifyingInformation} identity - indentity of user/app
   * @param {ICoordinates} coordinates - coordinates
   * @returns {Promise<IntersectionData>}
   */
  static getNearByRoadwayData(identity: BaseIdentifyingInformation, coordinates: ICoordinates): Promise<RoadwayData[]> {
    return quickJsonFetch<MappingRoadwayResponse>(
      identity.webServiceUrl + "/API/Mapping/GetNearByRoadwayData_FormIo",
      new PayloadWebRequest<ICoordinates>(identity, coordinates),
      identity.token
    ).then((data: MappingRoadwayResponse) => data.roadways);
  }

  static snapToRoadway(
    coordinates: ICoordinates,
    externalUrl: string,
    userId: number,
    applicationId: number
  ): Promise<ICoordinates> {
    let identity = {
      webServiceUrl: "",
      agencyOri: "",
      token: "",
      userId: userId,
      applicationId: applicationId,
      versionId: ""
    };
    let request = new PayloadWebRequest<ICoordinates>(identity, coordinates);

    return quickJsonFetch<MappingPointResponse>(externalUrl + "/API/Mapping/SnapToRoadway_FormIo", request, "").then(
      data => data.coordinates
    );
  }

  static findAddressCandidates(
    addressCandidate: IAddressCandidateViewModel,
    externalUrl: string,
    userId: number,
    applicationId: number
  ): Promise<ICoordinates> {
    let identity = {
      webServiceUrl: "",
      agencyOri: "",
      token: "",
      userId: userId,
      applicationId: applicationId,
      versionId: ""
    };
    let request = MappingAddressCandidateRequestViewModel.new(identity, addressCandidate);

    return quickJsonFetch<MappingPointResponse>(externalUrl + "/API/Mapping/FindAddressCandidates_FormIo", request, "").then(
      data => data.coordinates
    );
  }

  /**
   * @description Gets default zoom for a location.
   * @static
   * @param {Location} location
   * @param {number} [minimumZoom]
   * @returns {number}
   * @memberof MapUtils
   */
  static getDefaultZoom(location: LocationExtended, minimumZoom?: number): number {
    const hasTownship = location.townshipCode || location.townshipName;
    const hasCity = location.cityCode || location.cityName;
    const hasCounty = location.countyCode || location.countyName;

    if ((hasTownship || hasCity) && location.stateCode) {
      return 14;
    }

    if (hasCounty && location.stateCode) {
      return 10;
    }

    if (location.stateCode) {
      return 7;
    }

    if (hasCity || hasTownship) {
      return 16;
    }

    return minimumZoom || 15;
  }

  static getMapFieldNames(mapOptions: IMapOptions): string[] {
    return Object.getOwnPropertyNames(mapOptions).filter(key => key.endsWith("FieldName"));
  }

  static milesToFeet(miles: number): number {
    return miles * feetInMiles;
  }

  static feetToMiles(feet: number): number {
    return feet / feetInMiles;
  }

  static numberToStringFixed(num: number, fixedDigit: number): string {
    if (!num.toFixed) {
      num = Number(num);
    }

    return num.toFixed(fixedDigit);
  }

  static getTruncatedCityName = (mappedCityName: string) => {
    // we are truncating mapped cityname for a specific case, there might more case where name might come
    //differently but for now we its only IN, so added this logic to retrive the name
    return mappedCityName.indexOf(" ") > 0
      ? mappedCityName
          .substr(0, mappedCityName.indexOf(" "))
          .trim()
          .toUpperCase()
      : mappedCityName;
  };
}
